// Single entry point for browser geolocation. iOS Safari re-prompts on every
// full page load, so the outcome (coordinates or a refusal) is remembered for
// the session, and concurrent callers share one browser request.
export interface UserLatLong {
  lat: number;
  lng: number;
}

const CACHE_KEY = 'wl_geo_latlong';
const DENIED_KEY = 'wl_geo_denied';
const GEO_OPTIONS = { maximumAge: 600000 };

let inFlight: Promise<UserLatLong | null> | null = null;

function readCache(): UserLatLong | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const parsed = raw && JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.lat === 'number' &&
      typeof parsed.lng === 'number'
    ) {
      return { lat: parsed.lat, lng: parsed.lng };
    }
  } catch (e) {
    // storage unavailable or corrupt entry
  }
  return null;
}

function isDenied(): boolean {
  try {
    return sessionStorage.getItem(DENIED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function store(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // ignore storage failures
  }
}

export function getUserLatLong(): Promise<UserLatLong | null> {
  const cached = readCache();
  if (cached) {
    return Promise.resolve(cached);
  }
  if (!navigator.geolocation || isDenied()) {
    return Promise.resolve(null);
  }
  if (!inFlight) {
    inFlight = new Promise<UserLatLong | null>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const value = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          store(CACHE_KEY, JSON.stringify(value));
          inFlight = null;
          resolve(value);
        },
        (error) => {
          // Only a refusal (PERMISSION_DENIED = 1) is remembered; others retry
          if (error.code === 1) {
            store(DENIED_KEY, '1');
          }
          inFlight = null;
          resolve(null);
        },
        GEO_OPTIONS,
      );
    });
  }
  return inFlight;
}
